import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  ChannelPickerCountryItem,
  ChannelPickerData,
  ChannelPickerItem,
} from '../Types/ChannelPicker';
import { get, post, put } from '../Services/http';
import { triggerToast } from '../Services/Toast.service';
import { translate } from '../Services/translation';
import { createTypedAsyncThunk } from '../Utils/createTypedAsyncThunk';

interface ChannelPickerState {
  items: ChannelPickerItem[];
  countries: ChannelPickerCountryItem[];
  currentChannel: ChannelPickerItem;
  open: boolean;
  currentCountryCode: string;
  isLoading: boolean;
}

const DEFAULT_STATE: ChannelPickerState = {
  open: false,
  isLoading: false,
} as ChannelPickerState;

export const fetchChannelPickerData = createAsyncThunk<
  ChannelPickerData,
  string
>('channelPicker/fetchChannelPickerData', async (countryCode) => {
  const response = await get(
    `/api/channelpicker?countryCode=${countryCode}`
  );
  return await response.json();
});

export const setSelectedChannelAndCountry = createTypedAsyncThunk<
  { channelItem: ChannelPickerItem; changeChannel: boolean },
  {
    channelItem: ChannelPickerItem;
    countryCode: string;
  }
>(
  'channelPicker/setSelectedChannelAndCountry',
  async (payload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await put('/api/channelpicker', {
        countryCode: payload.countryCode,
        channelPickerItem: payload.channelItem,
      });
      const data = await res.json();

      return fulfillWithValue({
        channelItem: payload.channelItem,
        changeChannel: data.changeChannel as boolean,
      });
    } catch (err) {
      console.log(err);
      return rejectWithValue(err.message);
    }
  }
);

const channelPickerSlice = createSlice({
  name: 'channelPicker',
  initialState: DEFAULT_STATE,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChannelPickerData.fulfilled, (state, action) => {
      state.countries = action.payload.countries;
      state.items = action.payload.items;
      state.isLoading = false;
    });
    builder.addCase(fetchChannelPickerData.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchChannelPickerData.rejected, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(
      setSelectedChannelAndCountry.fulfilled,
      (_, action) => {
        if (action.payload.changeChannel) {
          location.href = action.payload.channelItem.url;
        } else {
          location.reload(); // reload, as only the country was changed
        }
      }
    );
    builder.addCase(setSelectedChannelAndCountry.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(setSelectedChannelAndCountry.rejected, (state) => {
      state.isLoading = false;
      triggerToast({
        title: translate('general.error'),
        text: translate('language-select.error-changing-country-language'),
        type: 'error',
        autoClose: false
      });
    });
  },
});

export const channelPicker = channelPickerSlice.actions;

export default channelPickerSlice.reducer;
